import React, { useEffect } from "react";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { BrandingProvider } from "./context/BrandingContext";
import { ContentProvider } from "./context/ContentContext";
import { initLocale } from "./i18n";
import Landing from "./pages/Landing";
import Login from "./pages/Login";
import Signup from "./pages/Signup";
import Onboarding from "./pages/Onboarding";
import Workspace from "./pages/Workspace";
import Admin from "./pages/Admin";
import AdminUserDetail from "./pages/AdminUserDetail";
import AdminFileDetail from "./pages/AdminFileDetail";
import Account from "./pages/Account";
import VerifyEmail from "./pages/VerifyEmail";
import Billing from "./pages/Billing";
import BillingSuccess from "./pages/BillingSuccess";
import Features from "./pages/Features";
import Pricing from "./pages/Pricing";

/**
 * Resets scroll on every route change so navigating between
 * Landing → Features → Pricing always starts at the top.
 */
function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

function ProtectedRoute({ children, requireAdmin = false }) {
  const { user } = useAuth();
  if (user === undefined) {
    return (
      <div className="h-screen w-screen grid place-items-center bg-background">
        <div className="text-sm font-mono text-muted-foreground animate-pulse">loading workspace…</div>
      </div>
    );
  }
  if (user === null) return <Navigate to="/login" replace />;
  if (requireAdmin && user.role !== "super_admin") return <Navigate to="/workspace" replace />;
  if (!requireAdmin && !user.onboarded && user.role !== "super_admin") return <Navigate to="/onboarding" replace />;
  return children;
}

export default function App() {
  useEffect(() => { initLocale(); }, []);
  return (
    <BrandingProvider>
    <ContentProvider>
    <AuthProvider>
      <BrowserRouter>
        <ScrollToTop />
        <Toaster richColors position="top-right" />
        <Routes>
          <Route path="/" element={<Landing />} />
          <Route path="/features" element={<Features />} />
          <Route path="/pricing" element={<Pricing />} />
          <Route path="/login" element={<Login />} />
          <Route path="/signup" element={<Signup />} />
          <Route path="/verify-email" element={<VerifyEmail />} />
          <Route
            path="/onboarding"
            element={
              <RequireAuthOnly>
                <Onboarding />
              </RequireAuthOnly>
            }
          />
          <Route
            path="/workspace"
            element={
              <ProtectedRoute>
                <Workspace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/workspace/:convId"
            element={
              <ProtectedRoute>
                <Workspace />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin"
            element={
              <ProtectedRoute requireAdmin>
                <Admin />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users/:userId"
            element={
              <ProtectedRoute requireAdmin>
                <AdminUserDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/admin/users/:userId/files/:fileId"
            element={
              <ProtectedRoute requireAdmin>
                <AdminFileDetail />
              </ProtectedRoute>
            }
          />
          <Route
            path="/account"
            element={
              <RequireAuthOnly>
                <Account />
              </RequireAuthOnly>
            }
          />
          <Route
            path="/billing"
            element={
              <RequireAuthOnly>
                <Billing />
              </RequireAuthOnly>
            }
          />
          <Route
            path="/billing/success"
            element={
              <RequireAuthOnly>
                <BillingSuccess />
              </RequireAuthOnly>
            }
          />
          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
      </BrowserRouter>
      </AuthProvider>
      </ContentProvider>
    </BrandingProvider>
  );
}

function RequireAuthOnly({ children }) {
  const { user } = useAuth();
  if (user === undefined) return null;
  if (user === null) return <Navigate to="/login" replace />;
  return children;
}
